/**
 * Product follow (`docs/product-follow.md`) on the compare page and the product hub. Signed in: the
 * reader's follows from DealHunt (one GET per session), switched per product. Signed out: the click
 * opens the sign-in and remembers the product; the follow completes when a session arrives, on this
 * page or the next one within `FOLLOW_PENDING_MS` (an email-link sign-in lands in another tab). That is the
 * Ask's arm-then-act pattern (`claim-after-sign-in.ts`) without the device claim: the signed
 * identity alone names the member, and DealHunt mints their profile on the first PUT.
 *
 * Client-only: localStorage and state set in effects, so the cached HTML never differs by reader.
 */
import { useCallback, useEffect, useState } from 'react'
import type { OwnerFollowItem } from 'lib/dealhunt/types'
import { ownerApi, OwnerApiError } from './api'
import {
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
  type OwnerLogInOptions,
} from './auth-bridge'

export const FOLLOW_PENDING_KEY = 'sg:follow:pending'
/** How long a signed-out click consents to the follow (the claim marker's window). */
export const FOLLOW_PENDING_MS = 30 * 60 * 1000

/** What a follow emails: the v1 event kinds (`successor` is v2), never a price. */
export const FOLLOW_COPY = {
  off: 'Follow',
  on: 'Following',
  offNote: 'Get an email when SoundGuys covers it or owners review it.',
  onNote: 'We’ll email you about new coverage and new owner reviews. Tap to unfollow.',
  failed: 'We couldn’t save your follow. Try again in a moment.',
  listEmpty: 'You don’t follow any products yet. Tap Follow on a product’s page or on the',
  listEmptyLink: 'compare page',
  listLoading: 'Loading…',
  listUnknown: 'Unknown product',
  unfollow: 'Unfollow',
} as const

const LOGIN: OwnerLogInOptions = {
  copy: {
    title: 'Follow this product',
    subtitle: 'Sign in and we’ll email you when SoundGuys covers it or owners review it.',
  },
}

function markPending(nodeId: string): void {
  try {
    window.localStorage.setItem(FOLLOW_PENDING_KEY, JSON.stringify({ nodeId, at: Date.now() }))
  } catch {
    // Storage off: the reader taps again once signed in.
  }
}

/** The product a signed-out click asked for, inside the window; consumes the marker. */
export function takeFollowPending(now = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(FOLLOW_PENDING_KEY)
    window.localStorage.removeItem(FOLLOW_PENDING_KEY)
    const v = raw ? (JSON.parse(raw) as { nodeId?: unknown; at?: unknown }) : null
    return v &&
      typeof v.nodeId === 'string' &&
      typeof v.at === 'number' &&
      now - v.at < FOLLOW_PENDING_MS
      ? v.nodeId
      : null
  } catch {
    return null
  }
}

function message(e: unknown): string {
  return e instanceof OwnerApiError && e.message ? e.message : FOLLOW_COPY.failed
}

/** The follow covering `nodeId` (a sibling line matches its canonical through `node_ids`). */
export function followFor(
  follows: readonly OwnerFollowItem[] | null,
  nodeId: string
): OwnerFollowItem | null {
  return follows?.find(f => f.node_ids.includes(nodeId)) ?? null
}

export interface Follows {
  /** The reader's follows; null = not read yet, or signed out. */
  follows: OwnerFollowItem[] | null
  isOn: (nodeId: string) => boolean
  busy: string | null
  error: { nodeId: string; message: string } | null
  toggle: (nodeId: string) => void
}

export function useFollows(): Follows {
  const session = useOwnerSessionOptional()
  const credential = useOwnerCredentialOptional()
  const [follows, setFollows] = useState<OwnerFollowItem[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<Follows['error']>(null)

  useEffect(() => {
    if (!credential) {
      setFollows(null)
      return undefined
    }
    let live = true
    const pending = takeFollowPending()
    const read = pending ? ownerApi.follow(credential, pending) : ownerApi.follows(credential)
    read
      .then(r => live && setFollows(r.follows))
      .catch(e => {
        if (live && pending) {
          setError({ nodeId: pending, message: message(e) })
        }
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one read per session token
  }, [credential?.token])

  const toggle = useCallback(
    (nodeId: string): void => {
      if (!credential) {
        markPending(nodeId)
        void session.showLogIn(LOGIN)
        return
      }
      if (busy) {
        return
      }
      const current = followFor(follows, nodeId)
      setBusy(nodeId)
      setError(null)
      const write = current
        ? ownerApi.unfollow(credential, current.node_id)
        : ownerApi.follow(credential, nodeId)
      write
        .then(r => setFollows(r.follows))
        .catch(e => setError({ nodeId, message: message(e) }))
        .finally(() => setBusy(null))
    },
    [credential, session, busy, follows]
  )

  return { follows, isOn: id => followFor(follows, id) !== null, busy, error, toggle }
}
